import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { style } from "./gridEditor.style";
import { CellData, CellState, ExportType, GridEditorAdapter, GridEditorOptions, RowData, allRowsLoaded } from "./gridEditorAdapter";
import { Grid, GridEditEvent, GridEvent, RowModel, SelectorEvent, PasteMode, CellModel, CellRange, MenuOptions, RowSource } from "@omegagrid/grid";
import { GridMenuEvent } from "@omegagrid/grid/dist/ui/grid.menu";
import { msg } from "@omegagrid/localize";
import { actions, Overlay, getThemeManager } from "@omegagrid/core";
import { Toolbar, ToolbarItem } from "@omegagrid/toolbar";
import { AppComponent } from "../component";
import { Polling } from "../container/polling";
import { Saveable } from "../types";

@customElement('rp-editor-grid')
export class GridEditor extends AppComponent implements Saveable {

	static styles = [style];

	@property({type: Object})
	adapter: GridEditorAdapter;

	@query('og-grid')
	grid: Grid;

	@query('og-overlay')
	loader: Overlay;

	@query('og-toolbar.toolbar')
	toolbar: Toolbar;

	@query('og-toolbar.custom-toolbar')
	customToolbar: Toolbar;

	@property({ type: Boolean })
	noToolbar = false;

	@state()
	toolbarItems: ToolbarItem[] = [];

	originalValues = new Map<CellModel, unknown>();
	deletedRows = new Set<RowModel>();
	newRows = new Set<RowModel>();
	options: GridEditorOptions;
	/** State of the auto-update toggle; the polling itself runs only while connected and the tab is visible. */
	autoUpdate = false;
	private _polling: Polling | null = null;
	saving = false;

	get isSaved() { return this.originalValues.size == 0 && this.newRows.size == 0 && this.deletedRows.size == 0 }

	layout() {
		this.grid?.layout();
	}

	get activeThemeType() { return getThemeManager().activeTheme.type }
	get activeTheme() {
		return this.activeThemeType === 'dark' ? this.options?.darkTheme : this.options?.lightTheme;
	}

	_lastThemeType: string;
	updateTheme = () => {
		if (this._lastThemeType === this.activeThemeType) return;
		this._lastThemeType = this.activeThemeType;
		this.grid.model.applyTheme(this.activeTheme, true);
		this.grid.rendering.render(true);
	};

	connectedCallback(): void {
		super.connectedCallback();
		getThemeManager().addEventListener('change', this.updateTheme);
		if (this._lastThemeType != null) this.updateTheme();
		if (this.autoUpdate) this._startAutoUpdate();
	}

	disconnectedCallback(): void {
		this._lastThemeType = this.activeThemeType;
		getThemeManager().removeEventListener('change', this.updateTheme);
		this._polling?.stop(); // a detached grid (closed or hidden tab) must not keep refreshing
		this.adapter?.dispose();
	}

	async willUpdate(props: Map<PropertyKey, unknown>) {
		this.originalValues.clear();
		if (props.has('adapter')) {
			this.adapter.editor = this;
			this.options = await this.adapter.getOptions();
			if (this.options?.autoUpdate) this.setAutoUpdate(true);
			this.createGridMenus();
			this.toolbarItems = await this.createToolbarItems();
		}
	}

	createGridMenus() {
		if (this.options?.menus || (!this.options?.rowMenu && !this.options?.customMenus)) return;

		const menus: MenuOptions = this.options?.customMenus || {
			row: { items: [], replace: true },
			cell: { items: [], replace: true },
			column: { items: [], replace: true }
		};

		if (this.options.rowMenu.addRows) menus.row.items.push({
			key: 'addRows',
			value: msg('Add rows'),
			icon: 'plus'
		});

		if (this.options.rowMenu.deleteRows) menus.row.items.push({
			key: 'deleteRows',
			value: msg('Delete rows'),
			icon: 'trash'
		});

		this.options.menus = menus;
	}

	async createToolbarItems() {
		const options = this.options;
		if (!options.toolbar) return [];
		const items: ToolbarItem[] = [];

		if (options.toolbar.refresh?.enabled) items.push({
			type: 'button',
			id: 'refresh',
			text: options.toolbar.refresh.text,
			color: options.toolbar.refresh.color,
			icon: options.toolbar.refresh.icon
		});

		if (options.toolbar.autoUpdate?.enabled) items.push({
			type: 'togglebutton',
			id: 'autoUpdate',
			value: this.autoUpdate,
			text: options.toolbar.autoUpdate.text,
			color: options.toolbar.autoUpdate.color,
			icon: options.toolbar.autoUpdate.icon
		});

		if (options.toolbar.save?.enabled) items.push({
			type: 'button',
			id: 'save',
			title: options.keyboardShortcuts ? '(Ctrl+S)' : null,
			text: options.toolbar.save.text,
			color: options.toolbar.save.color,
			icon: options.toolbar.save.icon
		});

		if (options.toolbar.discard?.enabled) items.push({
			type: 'button',
			id: 'discard',
			text: options.toolbar.discard.text,
			color: options.toolbar.discard.color,
			icon: options.toolbar.discard.icon
		});

		if (options.toolbar.new?.enabled) items.push({
			type: 'button',
			id: 'new',
			text: options.toolbar.new.text,
			color: options.toolbar.new.color,
			icon: options.toolbar.new.icon
		});

		if (options.toolbar.rowPasteMode?.enabled) {
			if (options.toolbar.rowPasteMode.value)
				this.options.clipboard.rowPasteMode = options.toolbar.rowPasteMode.value as PasteMode;
			items.push({
				type: 'dropdown',
				id: 'rowPasteMode',
				width: 135,
				value: this.options.clipboard.rowPasteMode,
				items: [
					{k: 'rewrite_append', v: msg('Rewrite mode')},
					{k: 'insert', v: msg('Insert mode')}
				]
			});
		}

		if (options.exportTypes?.length) {
			items.push({
				type: 'dropdown-menu',
				id: 'export',
				text: options.toolbar.export.text,
				width: 90,
				color: options.toolbar.export.color,
				icon: options.toolbar.export.icon,
				items: options.toolbar.export.items as any
			});
		}
		return items;
	}

	setCellValue(r: number, c: number, v: string|number|boolean, dv?: string) {
		const cell = this.grid.model.getCellByCoords(r, c);
		if (!cell) return;
		const oldValue = cell.value as string;
		cell.value = v;
		cell.displayValue = dv ?? null;
		cell.setProperty('edited', true);
		this._onEdit({
			editType: 'value',
			cells: [cell],
			oldValues: [oldValue],
			newValues: [v],
		} as GridEditEvent);
	}

	getRowData(all: boolean): RowData[] {
		const rowData: RowData[] = [];
		this.grid.model.rows.forEach(row => {
			if ((row.getProperty('deleted') && row.getProperty('new')) || row.getProperty('empty')) return;

			const state: CellState = (row.getProperty('deleted') ? 'deleted'
				: (row.getProperty('new') ? 'new'
					: (row.getProperty('edited') ? 'edited' : null)));

			if (state || all) {
				const data: Record<string, unknown> = {};
				for (const cell of row.model.getCellArray(row.range)) {
					data[cell.column.data || row.index.toString()] = cell.value;
				}
				rowData.push({data, state, index: row.index, props: row.properties});
			}
		});
		return rowData;
	}

	isCellEdited(cell: CellModel): boolean {
		return cell.properties?.edited === true;
	}

	getCellData(all: boolean): CellData[] {
		const cellData: CellData[] = [];
		this.grid.model.rows.forEach(row => {
			const deleted = !!row.getProperty('deleted');
			if ((deleted && row.getProperty('new')) || row.getProperty('empty')) return;

			for (const cell of row.model.getCellArray(row.range)) {
				const edited = this.isCellEdited(cell);
				if (all || edited || deleted) {
					cellData.push({
						r: row.index,
						c: cell.column.index,
						v: cell.value,
						state: deleted ? 'deleted' : edited ? 'edited' : undefined,
						props: Object.assign({}, row.properties, cell.column.properties, cell.properties)
					});
				}
			}
		});
		return cellData;
	}

	async refresh(confirmDiscard: boolean = false) {
		if (confirmDiscard && !this.isSaved) {
			const command = await this.host.openMenu({
				content: 'yes_no',
				text: msg('Discard changes and refresh?'),
			});
			if (command?.id == 'Y') {
				this.discard();
			} else {
				return;
			}
		}
		await this.grid.refresh();
	}

	async save() {
		if (this.saving) return;
		this.saving = true;
		this.showLoader();
		try {
			const options = this.grid.options as GridEditorOptions;
			let rowData: RowData[];
			let cellData: CellData[];
			switch (options.saveMode ?? 'edited_rows') {
				case 'edited_rows':
					rowData = this.getRowData(false);
					if (rowData.length > 0) await this.adapter.save(rowData);
					break;
				case 'all_rows':
					rowData = this.getRowData(true);
					if (rowData.length > 0) await this.adapter.save(rowData);
					break;
				case 'edited_cells':
					cellData = this.getCellData(false);
					if (cellData.length > 0) await this.adapter.save(cellData);
					break;
				case 'all_cells':
					cellData = this.getCellData(true);
					if (cellData.length > 0) await this.adapter.save(cellData);
					break;
				default:
					await this.adapter.save();
					break;
			}

			this.clearUpdates(false);
			if (options.refreshAfterSave) await this.refresh();
		} catch (e) {
			// Save failed: keep the pending edits (isSaved stays false) so the user can retry
			console.error('Grid save failed', e);
		} finally {
			this.saving = false;
			this.hideLoader();
		}
	}

	clearUpdates(revertValues: boolean) {
		const rows: RowModel[] = [];
		this.originalValues.forEach((value, cell) => {
			cell.setProperty('edited', false);
			rows.push(cell.row);
			if (revertValues) {
				cell.value = value;
				cell.displayValue = null;
			}
		});

		rows.forEach(row => {
			row.setProperty('edited', false);
			this.grid.rendering.updateCells(row.cells.filter(cell => cell));
		});

		if (revertValues) {
			this.newRows.forEach(row => this.grid.editing.deleteRows({index: row.index, count: 1}));
			this.deletedRows.forEach(row => {
				row.setProperty('deleted', false);
				this.grid.rendering.updateCells(row.cells.filter(cell => cell));
			});
		} else {
			this.newRows.forEach(row => {
				row.setProperty('new', false);
				this.grid.rendering.updateCells(row.cells.filter(cell => cell));
			});
			this.deletedRows.forEach(row => this.grid.editing.deleteRows({index: row.index, count: 1}));
		}

		this.newRows.clear();
		this.originalValues.clear();
		this.deletedRows.clear();

		this.dispatchEditEvent();
	}

	discard() {
		this.clearUpdates(true);
		this.adapter?.onAction('discard', null);
	}

	setRowPasteMode(mode: PasteMode) {
		this.grid.options.clipboard.rowPasteMode = mode;
	}

	setAutoUpdate = (on: boolean) => {
		this.autoUpdate = on;
		if (on) this._startAutoUpdate();
		else this._polling?.stop();
	}

	private _startAutoUpdate() {
		if (!this.isConnected) return; // started by connectedCallback
		const interval = this.options?.autoUpdateInterval || 30000;
		this._polling ??= new Polling(() => this.refresh(), interval);
		if (this._polling.interval !== interval) this._polling.interval = interval; // options may load after the first start
		this._polling.start();
	}

	get emptyRow(): RowModel | null {
		const rows = this.grid?.model?.rows ?? [];
		const lastRow = rows[rows.length - 1];
		return lastRow?.getProperty('empty') ? lastRow : null;
	}

	addEmptyRow() {
		if (this.emptyRow) return;
		if (!allRowsLoaded(this.grid.model)) return; // A paged grid gets the placeholder only after its last page

		const emptyRowIndex = this.grid.model.rows.length;
		this.grid.editing.insertRows({
			index: emptyRowIndex,
			after: false,
			count: 1,
			dispatchEvent: false,
		});
		const emptyRow: RowModel = this.grid.model.rows[emptyRowIndex];
		emptyRow.setProperty('empty', true);
		emptyRow._sortable = false;
		emptyRow._filterable = false;
		emptyRow.setStyleProperties({top: {style: 'double'}});
		// disable all action columns
		this.grid.model.columns.filter(column => column.type === 'a').forEach(column => {
			const cell = emptyRow.cell(column.index);
			if (cell) cell.type = 't';
		});
		this.grid.rendering.updateCells(emptyRow.cells.filter(cell => cell));
	}

	removeEmptyRow(row: RowModel) {
		if (!row.getProperty('empty') || !row.getProperty('edited')) return;

		row.setProperty('empty', false);
		row.setProperty('new', true);
		row._sortable = true;
		row._filterable = true;
		row.style = null;
		row.cells.forEach(cell => cell?.invalidateStyle());
		// enable all action columns
		this.grid.model.columns.filter(column => column.type === 'a').forEach(column => {
			const cell = row.cell(column.index);
			if (cell) cell.type = 'a';
		});
		this.newRows.add(row);
		this.addEmptyRow();
	}

	async export(type: ExportType) {
		this.showLoader();
		try {
			await this.adapter.export(type);
		}
		catch (e) {}
		finally {
			this.hideLoader();
		}
	}

	newRow(index: number, source?: RowSource) {
		while (this.grid.model.rows[index]?.getProperty('new')) {
			index++;
		}
		const [newRow] = this.grid.editing.insertRows({
			index: index,
			after: false,
			count: 1
		});
		if (source) {
			newRow.update(source);
			if (Array.isArray(source.data)) {
				source.data.forEach((v, c) => newRow.cell(c).value = v);
			} else if (typeof source.data === 'object') {
				const dataIndexMap = new Map<string|number, number>();
				this.grid.model.columns.forEach((col, c) => {
					if (col.data) dataIndexMap.set(col.data, c);
				});
				for (const [key, value] of Object.entries(source.data)) {
					const c = dataIndexMap.get(key) ?? (isNaN(Number(key)) ? null : Number(key));
					if (c != null) newRow.cell(c).value = value;
				}
			}
		}

		this.newRows.add(newRow);
		newRow.setProperty('new', true);

		this.grid.rendering.updateCells(newRow.cells.filter(cell => cell));
		this.adapter.onAction('addRow', newRow.cell(0));
		return newRow;
	}

	addRows(startIndex: number, count: number) {
		for (let i = 0; i < count; i++) {
			this.newRow(startIndex + i);
		}
	}

	deleteRow(row: number|RowModel) {
		row = typeof row === 'number' ? this.grid.model.rows[row] : row;
		if (row.getProperty('empty')) return;
		if (row.editable === false) return;
		if (row.getProperty('deleted')) {
			this.deletedRows.delete(row);
			row.setProperty('deleted', false);
		} else {
			this.deletedRows.add(row);
			row.setProperty('deleted', true);
		}
		this.grid.rendering.updateCells(row.cells.filter(cell => cell));
	}

	deleteRows(startIndex: number, count: number) {
		for (let i = 0; i < count; i++) {
			this.deleteRow(startIndex + i);
		}
	}

	duplicateRow(index: number) {
		const row = this.grid.model.rows[index];
		const source = row.getSourceData();
		source.data = {};
		this.grid.model.columns.forEach(col => {
			const cell = row.cell(col.index);
			if (!cell?.editable) return;
			source.data[cell.column.data || col.index] = cell.value;
		});
		this.newRow(index + 1, source);
	}

	showLoader() {
		this.loader.show(this);
	}

	hideLoader() {
		this.loader.hide();
	}

	dispatchEditEvent() {
		this.dispatchEvent(new CustomEvent(this.isSaved ? 'save' : 'edit', {bubbles: true, composed: true}));
	}

	private getRowOperationRange(e?: GridMenuEvent): { index: number; rowSize: number } {
		const selection = this.grid.selecting.selection.range;
		let { r1, r2 } = selection;
		if (r1 > r2) [r1, r2] = [r2, r1]; // TODO - please, fix containsRow method in omegagrid :-)
		// If event row is outside selection, operate only on that row
		if (e && !(r1 <= e.row.index && e.row.index <= r2)) return { index: e?.row.index || 0, rowSize: 1 };
		return { index: r1, rowSize: r2 - r1 + 1 };
	}

	private selectAll() {
		const r1 = 0;
		const c1 = this.grid.model.columns[0]?.type === 'a' ? 1 : 0; // in case first col is action col
		const r2 = this.grid.model.rows.length - (this.emptyRow ? 2 : 1); // exclude the empty row if present
		const c2 = this.grid.model.columns.filter(col => !col.hidden).length - 1; // hidden cols must be at the end!
		this.grid.selecting.select(new CellRange(c1, r1, c2, r2));
	}

	_onToolbarAction = (e: actions.ActionEvent) => {
		const parts = e.action.key.split('.');
		switch (parts[0]) {
			case 'refresh': this.refresh(true); break;
			case 'autoUpdate': this.setAutoUpdate(e.action.value as boolean); break;
			case 'new':
				this.newRow(0);
				this.dispatchEditEvent();
				break;
			case 'save': this.save(); break;
			case 'discard': this.discard(); break;
			case 'rowPasteMode': this.setRowPasteMode(e.action.value as PasteMode); break;
			case 'export': this.export(parts[1] as ExportType); break;
		}
	}

	_onCustomToolbarAction = (e: actions.ActionEvent) => {
		// the grid has no model until its rows are loaded - reading activeCell would throw and swallow the action
		const activeCell = this.grid?.model ? this.grid.selecting.activeCell : null;
		this.adapter.onAction(e.action.key, activeCell, e);
	}

	_onKeyDown = async (e: KeyboardEvent) => {
		if (!this.options?.keyboardShortcuts) return;
		// Ctrl + + to add new rows
		if (this.options?.rowMenu?.addRows && (e.ctrlKey && e.key == '+')) {
			e.preventDefault();
			const { index, rowSize } = this.getRowOperationRange();
			this.addRows(index, rowSize);
			this.dispatchEditEvent();
		}
		// Ctrl + - to delete selected rows
		if (this.options?.rowMenu?.deleteRows && (e.ctrlKey && e.key == '-')) {
			e.preventDefault();
			const { index, rowSize } = this.getRowOperationRange();
			this.deleteRows(index, rowSize);
			this.dispatchEditEvent();
		}
		// Ctrl + S to save
		if (this.options?.toolbar?.save?.enabled && (e.ctrlKey && e.key.toLowerCase() == 's')) {
			e.preventDefault();
			const command = await this.host.openMenu({
				content: 'yes_no',
				text: `${msg('Save changes')}?`,
			});
			if (command?.id == 'Y') this.save();
		}
		// Ctrl + A to select all (hidden columns are ignored, but must be at the end)
		if (e.ctrlKey && e.key.toLowerCase() == 'a') {
			e.preventDefault();
			this.selectAll();
		}
	}

	_onEdit = (e: GridEditEvent) => {
		if (!e.cells) return;
		const rows = new Set<RowModel>();
		e.cells.forEach((cell, i) => {
			if (!this.originalValues.has(cell)) this.originalValues.set(cell, e.oldValues[i]);
			const edited = this.originalValues.get(cell) != cell.value;
			cell.setProperty('edited', edited);
			if (!edited) this.originalValues.delete(cell);
			rows.add(cell.row);
		});
		rows.forEach(row => {
			let rowEdited = false;
			for (const cell of row.cells) {
				if (!cell) continue;
				if (this.isCellEdited(cell)) {
					rowEdited = true;
				}
			}
			row.setProperty('edited', rowEdited);
			this.grid.rendering.updateCells(row.cells.filter(cell => cell));
			if (this.options.showEmptyRow) this.removeEmptyRow(row);
		});

		this.dispatchEditEvent();
	}

	_onGridAction = (e: GridEvent) => {
		const row = e.cell.row;
		switch (e.action) {
			case 'insert':
			case 'new':
				this.newRow(row.index + 1, row?.getProperty('__defaults'));
				this.dispatchEvent(new CustomEvent('edit', {detail: e, bubbles: true, composed: true}));
				break;
			case 'duplicate':
				this.duplicateRow(row.index);
				this.dispatchEditEvent();
				break;
			case 'delete':
				this.deleteRow(row);
				this.dispatchEditEvent();
				break;
			default: this.adapter.onAction(e.action, e.cell);
		}
	}

	_onGridBeforeLoad = () => {
		this.showLoader();
	}

	_onGridAfterLoad = () => {
		const theme = this.activeTheme;
		if (theme) this.grid.model.applyTheme(theme);

		this.adapter?.afterLoad();
		if (this.options?.showEmptyRow && this.options?.editable !== false) {
			setTimeout(() => this.addEmptyRow(), 5); // delay to ensure grid model is ready
		}
		this.hideLoader();
	}

	_onGridAutofill = (e: SelectorEvent) => {
		const range = e.grid.selection.range;
		if (!range) return;
		const startCell = e.grid.model.getCellByRange(range);
		if (!startCell || !startCell.row.getProperty('deleted')) return;
		for (const cell of e.grid.model.getRangeIterator(e.selector.autofillRange)) {
			this.deleteRow(cell.row);
		}
	}

	_onGridMenuClick = (e: GridMenuEvent) => {
		const { index, rowSize } = this.getRowOperationRange(e);
		switch (e.item.key) {
			case 'addRows':
				this.addRows(index, rowSize);
				this.dispatchEditEvent();
				break;
			case 'deleteRows':
				this.deleteRows(index, rowSize);
				this.dispatchEditEvent();
				break;
			default: this.adapter.onAction(e.item.key.toString(), e.row?.cell(0) || null);
		}
	}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>

		${this.noToolbar ? '' : html`
			<div class="controls">
				<og-toolbar
					class="toolbar"
					.items="${this.toolbarItems}"
					@toolbar.action="${this._onToolbarAction}">
				</og-toolbar>

				${this.options?.customToolbar?.length > 0 ? html`
					<og-toolbar
						class="custom-toolbar"
						.items="${this.options.customToolbar}"
						@toolbar.action="${this._onCustomToolbarAction}">
					</og-toolbar>
				` : ``}
			</div>
		`}

		<div class="body">
			<og-grid
				.options="${this.options || {}}"
				.adapter="${this.adapter}"
				@grid.edit="${this._onEdit}"
				@keydown="${async (e: KeyboardEvent) => await this._onKeyDown(e)}"
				@grid.action="${this._onGridAction}"
				@grid.menu.click="${this._onGridMenuClick}"
				@grid.beforeLoad="${this._onGridBeforeLoad}"
				@grid.afterLoad="${this._onGridAfterLoad}"
				@grid.selector.autofill="${this._onGridAutofill}">
			</og-grid>
		</div>
	`;

}
